import type {
  LibraryAggregates,
  LibraryQuery,
  LibraryResponse,
  LibraryRoundCut,
  LibraryRoundGroups,
  OutcomeShares,
  RoundCutPrice,
} from "@screener/protocol";
import { isRecord } from "@/lib/guards";
import { DAEMON_REQUEST_TIMEOUT_MS } from "@/lib/request-timeout";

/**
 * HTTP-клиент панели библиотеки §9. Маршрут — `GET /api/library` запросного
 * канала §7.6 (design.md stage7-library-panel, решение 3): типы ответа — из
 * `@screener/protocol`, исходы выражены кодом HTTP, как у `density-api.ts`.
 */

const LIBRARY_PATH = "/api/library";

/**
 * Четыре исхода, не два. `401` уводит на форму входа, `400` — негодные
 * фильтры (клиент проверяет их сам, так что это рассинхрон с демоном, а не
 * ошибка человека), всё прочее — недоступность, при которой повтор осмыслен.
 */
export type LibraryResult =
  | { status: "ok"; value: LibraryResponse }
  | { status: "invalid" }
  | { status: "unauthorized" }
  /** `403 {"reason":"plan"}`: тариф библиотеку не даёт — это замок, а не ошибка. */
  | { status: "plan" }
  | { status: "unavailable" };

/**
 * Строка запроса из объекта контракта `LibraryQuery`. Имена параметров —
 * ключи типа, а не строки здесь: переименование в контракте ломает сборку.
 * Незаданные (`undefined`) ключи опускаются — демон отвергает пустое значение.
 */
export function buildLibraryQuery(query: LibraryQuery): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) {
      params.set(key, value);
    }
  }
  const text = params.toString();
  return text === "" ? "" : `?${text}`;
}

/**
 * Полнота проверяется компилятором: `satisfies Record<…, true>` не
 * собирается, если протокол заводит новый вариант цены разреза или новое
 * поле, а перечень его не знает (тот же приём, что `ALERT_TRIGGERS`).
 */
const ROUND_CUT_PRICES = {
  anchor_at_detection: true,
  anchor_at_end: true,
} satisfies Record<RoundCutPrice, true>;

const OUTCOME_SHARE_KEYS = {
  held: true,
  eaten: true,
  pulled: true,
  timeout: true,
  lost: true,
  stopped: true,
} satisfies Record<keyof OutcomeShares, true>;

const ROUND_GROUP_KEYS = {
  off_grid: true,
  whole: true,
  half: true,
  fine: true,
} satisfies Record<keyof LibraryRoundGroups, true>;

function isRoundCutPrice(value: unknown): value is RoundCutPrice {
  return typeof value === "string" && Object.hasOwn(ROUND_CUT_PRICES, value);
}

function isNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

/** Счётчик — неотрицательное целое: дробь или минус в нём — чужая форма. */
function isCount(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}

function isNullableNumber(value: unknown): value is number | null {
  return value === null || isNumber(value);
}

function isOutcomeShares(value: unknown): value is OutcomeShares {
  return (
    isRecord(value) && Object.keys(OUTCOME_SHARE_KEYS).every((key) => isNullableNumber(value[key]))
  );
}

function isLibraryAggregates(value: unknown): value is LibraryAggregates {
  return (
    isRecord(value) &&
    isCount(value.approaches) &&
    isCount(value.first_approaches) &&
    isCount(value.densities) &&
    isNullableNumber(value.mean_first_bounce_pct) &&
    isNullableNumber(value.mean_bounce_pct) &&
    isNullableNumber(value.mean_discount_pct) &&
    isOutcomeShares(value.outcome_shares)
  );
}

function isRoundGroups(value: unknown): value is LibraryRoundGroups {
  return (
    isRecord(value) && Object.keys(ROUND_GROUP_KEYS).every((key) => isLibraryAggregates(value[key]))
  );
}

function isRoundCut(value: unknown): value is LibraryRoundCut {
  return isRecord(value) && isRoundCutPrice(value.price) && isRoundGroups(value.groups);
}

function isLibraryResponse(value: unknown): value is LibraryResponse {
  if (!isRecord(value)) {
    return false;
  }
  const { total, round_at_detection: detection, round_at_end: end } = value;
  return (
    isLibraryAggregates(total) &&
    isRoundCut(detection) &&
    isRoundCut(end) &&
    // Разрез стоит со своей ценой: строка о недошедших берётся из первого, а
    // подпись — из цены, и расходиться им нельзя.
    detection.price === "anchor_at_detection" &&
    end.price === "anchor_at_end"
  );
}

/** Тело отказа `403` по тарифу: `{"reason":"plan"}`. Иное тело — обычный отказ. */
async function isPlanDenial(response: Response): Promise<boolean> {
  try {
    const body: unknown = await response.json();
    return isRecord(body) && body.reason === "plan";
  } catch {
    return false;
  }
}

/**
 * Выборка библиотеки по применённым фильтрам. `signal` — отмена устаревшего
 * запроса; таймаут накладывается поверх него. Любая отмена, как и сеть,
 * сводится к `unavailable`: решать, чей это был запрос, — дело вызывающего.
 */
export async function fetchLibrary(
  query: LibraryQuery,
  signal?: AbortSignal,
): Promise<LibraryResult> {
  const timeout = AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS);
  let response: Response;
  try {
    response = await fetch(`${LIBRARY_PATH}${buildLibraryQuery(query)}`, {
      signal: signal === undefined ? timeout : AbortSignal.any([signal, timeout]),
    });
  } catch {
    return { status: "unavailable" };
  }
  if (response.status === 401) {
    return { status: "unauthorized" };
  }
  if (response.status === 400) {
    return { status: "invalid" };
  }
  if (response.status === 403 && (await isPlanDenial(response))) {
    return { status: "plan" };
  }
  if (!response.ok) {
    console.error("выборка библиотеки отклонена демоном", response.status);
    return { status: "unavailable" };
  }
  try {
    const body: unknown = await response.json();
    if (!isLibraryResponse(body)) {
      console.error("выборка библиотеки: тело ответа чужой формы");
      return { status: "unavailable" };
    }
    return { status: "ok", value: body };
  } catch (error) {
    console.error("выборка библиотеки: тело ответа не разобралось как JSON", error);
    return { status: "unavailable" };
  }
}
